"use client";
import React, { useEffect, useRef, useState } from "react";
import styles from "./navbar.module.scss";
import Image from "next/image";
import horizontalLogo from "@public/horizontal-color-cropped.png";
import NavLinks, {
  LinkType,
  NavLinksProps,
} from "@/components/layout/navbar/navlinks";
import {
  LinkMenuBtn,
  MenuLinkProps,
} from "@/components/layout/navbar/menu-link-btn";
import Link from "next/link";
import MobileNav from "@/components/layout/navbar/mobile-nav";
import BlessMobileNavBtn from "@/components/layout/burger-menu/burger-menu";
import classNames from "classnames";
import { useUIStore } from "@/stores/ui-states.store";
import { throttle } from "@/utils/throttle";
import ConnectWalletBtn from "@/components/layout/connect-wallet-btn";
import ChainEnvSwitch from "@/components/layout/navbar/chain-env-switch";
import { featureFlags } from "@/config/feature-flags";
import { isMainnet } from "@/constants/flags.constant";
import { LINKS } from "@/constants/links.constant";
import { GiArchBridge, GiRadarSweep } from "react-icons/gi";
import {
  PiBookOpenTextLight,
  PiBridge,
  PiCoinVertical,
  PiVault,
} from "react-icons/pi";
import { VscMirror } from "react-icons/vsc";
import { MdRadar } from "react-icons/md";
import { LuWrench } from "react-icons/lu";
import { HiOutlineNewspaper } from "react-icons/hi2";
import { BsTicketPerforatedFill } from "react-icons/bs";

const docLinks: MenuLinkProps["links"] = [
  {
    url: LINKS.DOC.GENERAL,
    name: "General",
    prefixIcon: PiBookOpenTextLight,
    description:
      "Overview of features and benefits, with simple usage guidelines.",
    color: "lavender",
  },
  {
    url: LINKS.DOC.TECHNICAL,
    name: "Technical",
    prefixIcon: LuWrench,
    description:
      "In-depth API references, code examples, and integration details.",
    color: "dodgerBlue",
  },
  {
    url: LINKS.DOC.WHITEPAPER,
    name: "Whitepaper",
    prefixIcon: HiOutlineNewspaper,
    description:
      "Explore our comprehensive whitepaper for in-depth insights and analysis.",
    color: "default",
  },
];

const bridgeAppLinks: MenuLinkProps["links"] = [
  {
    name: "Bridge",
    prefixIcon: PiBridge,
    url: LINKS.APP.BLESS_BRIDGE,
    description: "Cross-chain BLESS token transfers made simple.",
    color: "greenYellow",
  },
  {
    name: "Bridge Scanner",
    prefixIcon: MdRadar,
    url: LINKS.APP.BRIDGE_SCANNER,
    description: "Monitor cross-chain transfers in real time.",
    color: "lavender",
  },
];

const coreAppLinks: MenuLinkProps["links"] = [
  ...(!isMainnet ? bridgeAppLinks : []),
  {
    name: "Modular Perms",
    url: LINKS.APP.MOD_PERMS,
    prefixIcon: PiVault,
    description: "Delegate specific actions securely across smart wallets.",
    color: "dodgerBlue",
  },
  {
    name: "Bless Dispenser",
    url: LINKS.APP.DISPENSER,
    prefixIcon: PiCoinVertical,
    description: "Get BLESS on Blessnet from other chains.",
    color: "dodgerBlue",
  },
  {
    name: "Ethereals",
    url: LINKS.APP.ETHEREALS,
    prefixIcon: VscMirror,
    description: "​Real-time mirrored assets from other chains.",
    color: "greenYellow",
  },
  {
    name: "Minter",
    url: LINKS.APP.MINTER,
    prefixIcon: BsTicketPerforatedFill,
    description: "Stay safe: mint on Blessnet, get NFTs on other chains.",
    color: "lavender",
  },
];

const navLinks: LinkType[] = [
  {
    name: "Home",
    url: LINKS.APP.ROOT,
  },
  {
    name: "Core Apps",
    direction: "right",
    columns: 3,
    childLinks: coreAppLinks,
  },
  {
    name: "Block Scanner",
    url: LINKS.APP.BLOCK_SCANNER,
  },
];

interface Props {
  showWalletConnect?: boolean;
  showChainEnvSwitch?: boolean;
}

const Navbar = ({
  showWalletConnect = false,
  showChainEnvSwitch = false,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const prevScrollVal = useRef<number>(0);
  const NAVBAR_SCROLL_THRESHOLD_TRIGGER = 100;
  const { setIsNavbarScrolled, isNavbarScrolled } = useUIStore();

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollVal = window.scrollY;
      if (prevScrollVal.current < scrollVal) {
        setIsNavbarScrolled(false);
        prevScrollVal.current = scrollVal;
        return;
      }
      if (scrollVal > NAVBAR_SCROLL_THRESHOLD_TRIGGER) {
        setIsNavbarScrolled(true);
      } else setIsNavbarScrolled(false);
      prevScrollVal.current = scrollVal;
    }, 200);

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [setIsNavbarScrolled]);

  function handleMenu() {
    setMenuOpen((prev) => !prev);
    if (!menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }

  return (
    <nav
      className={classNames(
        styles.navbarContainer,
        (isNavbarScrolled || menuOpen) && styles.scrolled,
      )}
    >
      <div className={styles.navbar}>
        <div className={styles.left}>
          <Link
            href={LINKS.APP.ROOT}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Image src={horizontalLogo} alt="blessnet-logo" width={150} />
          </Link>
          <NavLinks links={navLinks} />
        </div>
        <div className={styles.right}>
          <div className={styles.links}>
            <LinkMenuBtn
              links={docLinks}
              name="Blessnet Docs"
              prefixIcon={PiBookOpenTextLight}
              type="solid"
            />
          </div>
          {showWalletConnect && <ConnectWalletBtn />}
          {showChainEnvSwitch && featureFlags.enabledNavbarNetworkEnvSwitch && (
            // Excluded chain control is coupled to display of the wallet connect btn
            <ChainEnvSwitch />
          )}
        </div>
        <div className={styles.navMenuButtonContainer}>
          <BlessMobileNavBtn onClick={handleMenu} isOpen={menuOpen} />
        </div>
      </div>
      <MobileNav isOpen={menuOpen} />
    </nav>
  );
};

export default Navbar;
